import './styles.scss';

import { ReactElement, ReactNode } from 'react';
import { getClasses, titleCase, unSnakeCase } from '@/utils';

import { DynamicCellRenderer } from '@/common/Table/cells/DynamicCell';

export type StatusName = 'PASS' | 'FAIL' | 'MORE_TRAINING_REQUIRED' | 'PENDING' | 'IN_PROGRESS';

export const STATUS_COLORS: Record<StatusName, { text: string; background: string }> = {
  PASS: { text: '#008444', background: '#E6F8EF' },
  FAIL: { text: '#C00403', background: '#FCEEEE' },
  MORE_TRAINING_REQUIRED: { text: '#BB4D00', background: '#FFF4C6' },
  PENDING: { text: '#21272A', background: '#F2F4F8' },
  IN_PROGRESS: { text: '#0B4ED3', background: '#DDE8FF' },
};

export const STATUS_PROGRESS_COLORS: Record<StatusName, string> = {
  PASS: '#008444',
  MORE_TRAINING_REQUIRED: '#008444',
  FAIL: '#C00403',
  IN_PROGRESS: '#0B4ED3',
  PENDING: 'transparent',
};

export const statusLabel = (status: string): string => titleCase(unSnakeCase(status));

export type StatusCellProps = {
  status: StatusName;
  className?: string;
};

const StatusCell = ({ status, className }: StatusCellProps): ReactElement => {
  const { text, background } = STATUS_COLORS[status];
  return (
    <span
      className={getClasses('StatusCell', 'badge', 'rounded-pill', className)}
      style={{ color: text, backgroundColor: background }}
      data-status={status}
    >
      {statusLabel(status)}
    </span>
  );
};

export default StatusCell;

/**
 * Value pattern agreed with BE: `STATUS::<name>`, e.g. "STATUS::PASS".
 * `<name>` must be one of the StatusName keys above and picks the pill's colors + label.
 */
const STATUS_PATTERN = new RegExp(`^STATUS::(${Object.keys(STATUS_COLORS).join('|')})$`);

export type ParsedStatusValue = Pick<StatusCellProps, 'status'>;

export const parseStatusValue = (value: string): ParsedStatusValue | null => {
  const match = value.match(STATUS_PATTERN);
  if (!match) return null;

  const [, status] = match;
  return { status: status as StatusName };
};

// Utility for providing the progress-bar with a color depending on the status
export const progressColorFromValue = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const parsed = parseStatusValue(value);
  return parsed ? STATUS_PROGRESS_COLORS[parsed.status] : undefined;
};

export const statusCellRenderer: DynamicCellRenderer = {
  test: (value: string) => STATUS_PATTERN.test(value),
  render: (value: string): ReactNode => {
    const parsed = parseStatusValue(value);
    return parsed ? <StatusCell {...parsed} /> : value;
  },
};
